/**
 * OpenClaw 全局状态：会话级聊天数据，路由/页面切换时按 sessionId 保留与恢复
 * @module store/modules/openClaw
 */
import { post } from './sse';
import api, { OPEN_CLAW_CHAT_API } from '@/apis/openClawApi';
import { getStorageAuthInfo } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { buildHeaders } from '@/apis/openClawApi/request';
import {
  generateReasoningTemplate,
  generatePluginCallTemplate,
  generateMessageTemplate,
  generateSystemMessageTemplate,
  transformHistoryMessage
} from '@/pages/aiAssistant/taskMode/chat/components/chatMain/transformContentUtils.js';
import { cloneDeep } from 'lodash-es';
import i18n from '@/plugins/i18n';
import { emitter } from '@/plugins/emitter';
import dayjs from 'dayjs';
import { getAssignedMachine, hasAvailableResource } from '@/jmHttp/api/openClaw';
import { NEWBIE_TASK_BIZ_TYPE } from '@/constants/activityNbi';
import { reportNewbieTask } from '@/utils/newbieTaskReport';
import slsLogger from '@/utils/slsLogger';

/**
 * 在临时消息列表中按工具 call_id 查找 plugin_call 消息
 * @param {Array} tempMessageList
 * @param {string} callId
 * @returns {object|undefined}
 */
function findPluginCallMessageByCallId(tempMessageList, callId) {
  if (!callId || !Array.isArray(tempMessageList)) return undefined;
  return tempMessageList.find((item) => item.type === 'plugin_call' && item.content?.[0]?.data?.call_id === callId);
}

/** plugin_call 消息上 content[0].data，缺省时避免继续写入导致 TypeError */
function getPluginCallContentDataRow(msg) {
  return msg?.content?.[0]?.data;
}

/**
 * 处理 SSE type=data：按 call_id 命中对应 plugin_call，否则 in_progress 时回落到列表最后一项
 * @param {Array} tempMessageList
 * @param {string} status
 * @param {object} data
 */
function applyOpenClawDataTypeChunk(tempMessageList, status, data) {
  if (!data || !Array.isArray(tempMessageList)) return;
  const lastMsg = tempMessageList[tempMessageList.length - 1];

  if (status === 'in_progress') {
    const target =
      findPluginCallMessageByCallId(tempMessageList, data.call_id) ??
      (lastMsg?.type === 'plugin_call' ? lastMsg : undefined);
    if (target?.type !== 'plugin_call') return;
    const row = getPluginCallContentDataRow(target);
    if (!row) return;
    if ('call_id' in data) row.call_id = data.call_id;
    if ('name' in data) row.name = data.name;
    if ('arguments' in data) row.arguments = data.arguments;
    if ('output' in data) row.output = data.output;
    return;
  }

  if (status === 'completed') {
    let target;
    if (data.call_id) {
      target = findPluginCallMessageByCallId(tempMessageList, data.call_id);
    } else if (lastMsg?.type === 'plugin_call') {
      target = lastMsg;
    }
    if (target?.type !== 'plugin_call') return;
    const row = getPluginCallContentDataRow(target);
    if (!row) return;
    target.status = status;
    if ('output' in data) row.output = data.output;
  }
}

/**
 * 处理 SSE type=plugin_call_output：按 content[0].data.call_id 命中 plugin_call 再写 output
 * @param {Array} tempMessageList
 * @param {string} status
 * @param {Array} content
 * @param {object} data
 */
function applyOpenClawPluginCallOutputChunk(tempMessageList, status, content, data) {
  if (!Array.isArray(tempMessageList)) return;
  const lastMsg = tempMessageList[tempMessageList.length - 1];
  const callId = content?.[0]?.data?.call_id;

  if (status === 'in_progress') {
    const matchedOut = findPluginCallMessageByCallId(tempMessageList, callId);
    const fallbackOut = lastMsg?.type === 'plugin_call' ? lastMsg : undefined;
    const target = matchedOut ?? fallbackOut;
    if (target?.type !== 'plugin_call') return;
    const row = getPluginCallContentDataRow(target);
    if (!row) return;
    if (data && 'content' in data) row.output = data.content;
    return;
  }

  if (status === 'completed' && content?.length) {
    const out = content[0]?.data?.output;
    let target;
    if (callId) {
      target = findPluginCallMessageByCallId(tempMessageList, callId);
    } else if (lastMsg?.type === 'plugin_call') {
      target = lastMsg;
    }
    if (target?.type !== 'plugin_call') return;
    const row = getPluginCallContentDataRow(target);
    if (!row) return;
    target.status = status;
    if (out !== undefined) row.output = out;
  }
}

/** 断网后自动重连的总时长窗口：窗口内静默重连，超时仍未恢复才提示错误 */
const RECONNECT_WINDOW_MS = 30000;
/** 自动重连窗口内每次「调用重试接口（getSession）」的轮询间隔 */
const RECONNECT_POLL_INTERVAL_MS = 3000;
/** 会生成或改写工作台文件的工具名；命中后通知任务模式自动展开工作台。 */
const FILE_GENERATION_TOOL_NAMES = new Set(['write_file', 'edit_file', 'create_file']);
/**
 * 按 sessionId 维护的自动重连控制器：仅保存截止时间、定时器与在途标记，
 * 不放入 Vuex 响应式 state，避免定时器对象被持久化 / 序列化。
 * @type {Map<string, { deadline: number, timer: ReturnType<typeof setTimeout> | null, inFlight: boolean }>}
 */
const streamReconnectControllers = new Map();
const emittedFileGenerationCalls = new Set();

function isFileGenerationToolName(name) {
  return FILE_GENERATION_TOOL_NAMES.has(String(name || '').trim());
}

function emitFileGeneratedOnce(sessionId, chunk) {
  const toolName = chunk?.data?.name || chunk?.content?.[0]?.data?.name;
  // 触发条件（满足其一）：
  //  1. 命中已知文件生成工具名（write_file 等）——快速路径，工具刚发起即可预判；
  //  2. 任意工具产出 plugin_call_output——覆盖自定义 / 中文命名工具（如「写入文件」），
  //     避免因工具名不在白名单而漏开工作台。最终是否展开仍由 openWorkbenchWhenFilesReady
  //     经文件接口校验真实文件后决定，无文件则不会误开。
  const isPluginCallOutput = chunk?.type === 'plugin_call_output';
  if (!isFileGenerationToolName(toolName) && !isPluginCallOutput) return;

  const callId = chunk?.data?.call_id || chunk?.content?.[0]?.data?.call_id || chunk?.id || toolName || chunk?.type;
  const eventKey = `${sessionId}:${callId}`;
  if (emittedFileGenerationCalls.has(eventKey)) return;
  emittedFileGenerationCalls.add(eventKey);
  emitter.emit('openClaw/fileGenerated', { sessionId, toolName });
}

/**
 * 由 session_id 解析 /chats/:id 接口所需的会话主键 id。
 * 注意：流式接口用的是 session_id，而 getSession 用的是会话的 id（与路由 chatId 一致），
 * 两者是不同标识，sessionList 里通过 sessionId → id 建立映射；未命中时回退 session_id 兜底。
 * @param {object} context - Vuex action 上下文
 * @param {string} sid - session_id（当前会话）
 * @returns {string} 会话主键 id（chatId）
 */
function resolveChatId(context, sid) {
  const session = context.state.sessionList.find((item) => item.sessionId === sid);
  return session?.id || sid;
}

/**
 * 写入任务模式流式日志，统一组件名便于在 brm-console 中串联排查。
 * @param {'info'|'error'|'warn'} level 日志级别
 * @param {string} message 日志说明
 * @param {object} [extra] 附加排查字段
 * @returns {void}
 */
function logOpenClawStream(level, message, extra = {}) {
  slsLogger.console({
    component: 'OpenClawStream',
    level,
    message,
    extra
  });
}

/**
 * 清理指定会话的自动重连控制器与其定时器。
 * 用于重连成功、会话切换、用户停止等场景，确保不再继续轮询重试。
 * @param {string} sessionId - 会话 ID
 * @returns {void}
 */
function clearReconnectController(sessionId) {
  const ctrl = streamReconnectControllers.get(sessionId);
  if (!ctrl) return;
  clearTimeout(ctrl.timer);
  streamReconnectControllers.delete(sessionId);
}

/**
 * 安排指定会话的下一次重连尝试（先清掉旧定时器，保证同一会话只有一个待执行尝试）。
 * @param {object} context - Vuex action 上下文
 * @param {string} sid - 会话 ID
 * @param {number} delay - 延迟毫秒数
 * @returns {void}
 */
function scheduleReconnectAttempt(context, sid, delay) {
  const ctrl = streamReconnectControllers.get(sid);
  if (!ctrl) return;
  clearTimeout(ctrl.timer);
  ctrl.timer = setTimeout(() => runReconnectAttempt(context, sid), delay);
}

/**
 * 自动重连超时收尾：清理控制器并落地错误气泡，让用户看到「服务繁忙/恢复任务」。
 * @param {object} context - Vuex action 上下文
 * @param {string} sid - 会话 ID
 * @returns {void}
 */
function failStreamReconnect(context, sid) {
  clearReconnectController(sid);
  logOpenClawStream('error', '任务模式-流式自动重连超时', {
    sessionId: sid
  });
  context.commit('setStatus', { status: 'completed', sessionId: sid });
  context.commit('setChatLoading', { chatLoading: false, sessionId: sid });
  // 冻结已接收的部分内容，避免临时引用残留在 messageList 中
  const chat = context.state.sessionChatMap[sid];
  if (chat?.tempMessageList?.length > 0) {
    context.commit('chatDone', { sessionId: sid });
  }
  context.commit('addAbortMessage', {
    content: i18n.global.t('openClaw.chatPage.serverBusy'),
    sessionId: sid,
    messageType: 'error',
    role: 'system'
  });
}

/**
 * 自动重连窗口内的单次尝试：
 * 1. 超过 30s 窗口仍未恢复 → 回落到错误提示；
 * 2. 上一次重连尝试仍在途 → 等待其 _open/_error 结果，不重复发起；
 * 3. 调用重试接口 getSession 探测会话状态：
 *    - running：恢复流式（reconnect 成功由 _open 清理控制器，失败由 _error 续连）；
 *    - 其他（已结束）：拉取历史替换并结束重连，无需报错；
 *    - 请求失败（仍断网）：等待下一轮轮询。
 * @param {object} context - Vuex action 上下文
 * @param {string} sid - 会话 ID
 * @returns {Promise<void>}
 */
async function runReconnectAttempt(context, sid) {
  const ctrl = streamReconnectControllers.get(sid);
  if (!ctrl) return;

  // 超过 30s 窗口仍未恢复：回落到错误提示
  if (Date.now() >= ctrl.deadline) {
    failStreamReconnect(context, sid);
    return;
  }

  // 上一次重连尝试仍在进行中：等待其结果，先不重复发起，仅继续守护窗口
  if (ctrl.inFlight) {
    scheduleReconnectAttempt(context, sid, RECONNECT_POLL_INTERVAL_MS);
    return;
  }

  // 会话已切换走：交由 closeSessionSse 清理，这里不再处理
  if (sid !== context.state.currentSessionId) return;

  try {
    // getSession 用会话主键 id（=路由 chatId），不能直接用 session_id
    const chatId = resolveChatId(context, sid);
    const sessionData = await api.getSession(chatId);
    // 控制器可能在等待请求期间被清理（重连成功 / 切换会话）
    const curCtrl = streamReconnectControllers.get(sid);
    if (!curCtrl) return;

    logOpenClawStream('info', '任务模式-自动重连检测中', {
      sessionId: sid,
      chatId,
      sessionData
    });

    if (sessionData?.status === 'running') {
      // 任务仍在运行：恢复流式输出；sendMessage 内部会先关闭旧 SSE，无需额外清理
      curCtrl.inFlight = true;
      logOpenClawStream('info', '任务模式-自动重连检测到任务运行中', {
        sessionId: sid,
        chatId,
        sessionData
      });
      context.dispatch('sendMessage', { reconnect: true });
      // 兜底：reconnect 若长时间无 _open/_error 回执，仍按间隔继续检查窗口
      scheduleReconnectAttempt(context, sid, RECONNECT_POLL_INTERVAL_MS);
      return;
    }

    if (sessionData) {
      // 任务已结束（completed/aborted 等）：拉取历史替换，结束重连且不报错
      const historyMessages = transformHistoryMessage(sessionData.messages || []);
      context.commit('replaceSessionMessageList', { sessionId: sid, messageList: historyMessages });
      context.commit('setStatus', { status: 'completed', sessionId: sid });
      context.commit('setChatLoading', { chatLoading: false, sessionId: sid });
      clearReconnectController(sid);
      logOpenClawStream('info', '任务模式-自动重连检测到任务已结束', {
        sessionId: sid,
        chatId,
        status: sessionData.status,
        historyMessages,
        sessionData
      });
      return;
    }
  } catch (error) {
    logOpenClawStream('warn', '任务模式-自动重连轮询失败', {
      sessionId: sid,
      errorMessage: error?.message
    });
    // 仍断网 / 请求失败：什么都不做，落到下方等待下一轮
  }

  // 仍未恢复：安排下一次尝试
  scheduleReconnectAttempt(context, sid, RECONNECT_POLL_INTERVAL_MS);
}

/** 默认会话聊天 */
const defaultSessionChat = () => ({
  // SSE 实例
  sseInstance: null,
  messageList: [],
  tempMessageList: [],
  chatLoading: false,
  status: 'completed',
  expandedCollapseKeys: {},
  isRetrying: false,
  hasError: false
});

export default {
  namespaced: true,
  state: {
    /** 按 sessionId 存储的会话聊天状态，便于路由切换后按 sessionId 恢复 */
    sessionChatMap: {},
    // 当前会话ID
    currentSessionId: '',
    // 界面显示的会话ID
    userSelectedSessionId: '',
    // 会话列表
    sessionList: [],
    /**
     * 技能市场页分组筛选缓存：'all' | '__ungrouped__' | 分组 id（与 groupList.id / 技能 tags 一致）
     * 用于删除、批量删除、编辑保存刷新列表后恢复当前分组；null 表示未写入过，按「全部」处理。
     * 当前归属：聊天/研究模式下「工具与连接 → 技能」(toolsConnection/skills/) 使用。
     */
    skillsPageGroupFilter: null,
    /**
     * 任务模式 Agent 技能页（taskMode/agentSkills/）分组筛选缓存，与 skillsPageGroupFilter 同含义。
     * 拆出独立字段是为了让任务模式与「工具与连接 → 技能」物理隔离，
     * 切换两个入口时不会互相覆盖各自上次选中的分组。
     */
    taskModeAgentSkillsPageGroupFilter: null,
    /** 云电脑（分配虚拟机）到期时间戳（毫秒）；与 localStorage `assignedMachineTimestamp` 同步维护 */
    assignedMachineTimestamp: 0
  },
  getters: {
    /** 获取指定 sessionId 的聊天状态，不存在则返回 null */
    getSessionChat(state) {
      return (sessionId) => {
        if (!sessionId) return null;
        return state.sessionChatMap[sessionId || state.currentSessionId] ?? null;
      };
    },
    // 获取当前会话ID
    getCurrentSessionId(state) {
      return state.currentSessionId;
    },
    /** 获取指定 sessionId 的会话消息列表，不存在则返回空数组 */
    getSessionMessageList(state) {
      return (sessionId) => {
        if (!sessionId) return [];
        return state.sessionChatMap[sessionId || state.currentSessionId]?.messageList ?? [];
      };
    },
    /** 获取当前对话状态 */
    getCurrentChatStatus(state) {
      return state.sessionChatMap[state.currentSessionId]?.status ?? '';
    },
    /** 获取当前对话加载状态 */
    getChatLoading(state) {
      return state.sessionChatMap[state.currentSessionId]?.chatLoading ?? false;
    },
    /** 获取用户选择的会话ID */
    getUserSelectedSessionId(state) {
      return state.userSelectedSessionId;
    },
    /** 获取当前通道 */
    getCurrentChannel(state) {
      // 判断会话列表中是否存在当前会话ID，如果存在则返回会话的通道
      const session = state.sessionList.find((item) => item.sessionId === state.userSelectedSessionId);
      if (session) {
        return session.channel || 'console';
      }
      return 'console';
    },
    /** 获取当前通道的user_id */
    getCurrentChannelUserId(state) {
      const session = state.sessionList.find((item) => item.sessionId === state.userSelectedSessionId);
      if (session) {
        return session.user_id || session.userId || '';
      }
      return '';
    },
    getAssignedMachineTimestamp(state) {
      const assignedMachineTimestamp = window.localStorage.getItem('assignedMachineTimestamp') || 0;
      if (assignedMachineTimestamp) {
        state.assignedMachineTimestamp = Number(assignedMachineTimestamp);
      }
      return state.assignedMachineTimestamp;
    }
  },
  mutations: {
    /**
     * 设置会话列表
     * @param {array} sessionList - 会话列表
     * @returns {void}
     * @description 设置会话列表，如果未传入会话列表，则抛出错误
     */
    setSessionList(state, sessionList) {
      state.sessionList = sessionList;
    },
    /**
     * 技能页分组筛选记忆（与 skills/index 中 searchTags 语义一致）
     * 当前归属：聊天/研究模式下「工具与连接 → 技能」
     * @param {'all'|'__ungrouped__'|string|null} filter
     */
    setSkillsPageGroupFilter(state, filter) {
      state.skillsPageGroupFilter = filter;
    },
    /**
     * 任务模式 Agent 技能页分组筛选记忆，与 setSkillsPageGroupFilter 语义一致。
     * @param {'all'|'__ungrouped__'|string|null} filter
     */
    setTaskModeAgentSkillsPageGroupFilter(state, filter) {
      state.taskModeAgentSkillsPageGroupFilter = filter;
    },
    /**
     * 云电脑到期时间戳（毫秒）；与 localStorage `assignedMachineTimestamp` 同步
     * @param {number|string} value
     */
    setAssignedMachineTimestamp(state, value) {
      const n = Number(value);
      const ts = Number.isFinite(n) ? n : 0;
      state.assignedMachineTimestamp = ts;

      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('assignedMachineTimestamp', String(ts));
      }
    },
    /**
     * 设置 SSE 实例
     * @param {object} sseInstance - SSE 实例
     * @returns {void}
     * @description 设置 SSE 实例，如果未传入 SSE 实例，则抛出错误
     */
    setSseInstance(state, { sseInstance, sessionId }) {
      state.sessionChatMap[sessionId || state.currentSessionId].sseInstance = sseInstance;
    },
    /**
     * 设置当前会话ID
     * @param {string} sessionId - 会话ID
     * @returns {void}
     * @description 设置当前会话ID，如果未传入会话ID，则生成一个唯一会话ID 时间戳
     */
    setCurrentSessionId(state, sessionId) {
      // 显式传 null 表示清空当前会话（如删除/切换工作区），直接置空不生成随机 ID
      if (sessionId === null) {
        state.currentSessionId = null;
        return;
      }
      // sessionId 为 undefined 或空字符串时，视为新建会话，自动生成唯一 ID
      const newId = sessionId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      state.currentSessionId = newId;
      // 新建会话时，若该 ID 尚无缓存则初始化干净状态，
      // 防止 sessionChatMap 中同 key 的残留缓存数据污染新会话
      if (!sessionId && !state.sessionChatMap[newId]) {
        state.sessionChatMap[newId] = defaultSessionChat();
      }
    },
    /**
     * 设置用户选择的会话ID 控制台切换会话时使用
     * @param {string} sessionId - 会话ID
     * @returns {void}
     * @description 设置用户选择的会话ID，如果未传入会话ID，则抛出错误
     */
    setUserSelectedSessionId(state, sessionId) {
      state.userSelectedSessionId = sessionId;
    },
    /**
     * 初始化会话
     */
    initSession(state, { sessionId }) {
      if (!state.currentSessionId) {
        state.currentSessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }
      state.sessionChatMap[sessionId || state.currentSessionId] = defaultSessionChat();
    },
    /**
     * 设置会话
     */
    setSessionMessageList(state, messageList) {
      if (!state.sessionChatMap[state.currentSessionId]) {
        state.sessionChatMap[state.currentSessionId] = defaultSessionChat();
      } else {
        // 如果会话列表存在，则不进行设置 防止重复设置 本地已经是最新的
        return;
      }
      state.sessionChatMap[state.currentSessionId].messageList = cloneDeep(messageList);
      state.sessionChatMap[state.currentSessionId].tempMessageList = [];
    },
    /**
     * 移除消息列表末尾的错误气泡（仅当最后一条是 messageType=error 的系统消息时）
     * 用于 reconnect 恢复流式前清理残留错误提示，避免新旧内容同时出现。
     * 错误消息由 addAbortMessage 写入，始终位于数组内：
     *   - 有其他内容时追加进末尾数组（[content..., errorMsg]）
     *   - 无其他内容时包装成新数组（[errorMsg]）推入 messageList
     */
    removeLastErrorMessage(state, { sessionId } = {}) {
      const sid = sessionId || state.currentSessionId;
      const chat = state.sessionChatMap[sid];
      if (!chat?.messageList?.length) return;
      const last = chat.messageList[chat.messageList.length - 1];
      if (Array.isArray(last)) {
        const lastInArr = last[last.length - 1];
        if (lastInArr?.messageType === 'error') {
          if (last.length === 1) {
            // 数组只剩这一条错误消息，整个 slot 移除
            chat.messageList.pop();
          } else {
            // 数组里还有正常内容，仅移除末尾错误条目
            last.pop();
          }
        }
      } else if (last?.messageType === 'error') {
        chat.messageList.pop();
      }
    },
    replaceSessionMessageList(state, { sessionId, messageList }) {
      const sid = sessionId || state.currentSessionId;
      if (!sid) return;
      if (!state.sessionChatMap[sid]) {
        state.sessionChatMap[sid] = defaultSessionChat();
      }
      state.sessionChatMap[sid].messageList = cloneDeep(messageList || []);
      state.sessionChatMap[sid].tempMessageList = [];
    },
    /**
     * 向消息列表头部追加历史消息（用于渐进式加载）
     */
    prependSessionMessages(state, { sessionId, messages }) {
      const chat = state.sessionChatMap[sessionId || state.currentSessionId];
      if (!chat) return;
      chat.messageList.unshift(...cloneDeep(messages));
    },
    /**
     * 添加用户消息
     */
    addUserMessage(state, { content, attachments, metadataTimestamp = dayjs().format('YYYY-MM-DD HH:mm:ss') }) {
      // 判断是否存在当前会话ID，如果不存在，则生成一个唯一会话ID 时间戳（mutation 内不能 this.commit，直接改 state）
      if (!state.currentSessionId) {
        state.currentSessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }
      // 判断是否存在当前会话ID，如果不存在，则初始化会话
      if (!state.sessionChatMap[state.currentSessionId]) {
        state.sessionChatMap[state.currentSessionId] = defaultSessionChat();
      }
      state.sessionChatMap[state.currentSessionId].messageList.push({
        id: String(new Date().getTime()) + '_' + Math.random().toString(36).substring(2, 15),
        role: 'user',
        status: 'completed',
        datetime: metadataTimestamp,
        metadataTimestamp,
        type: 'message',
        content,
        sessionId: state.currentSessionId,
        // 保存附件信息用于消息气泡展示
        attachments: attachments && attachments.length > 0 ? attachments : undefined
      });
    },
    /*
     * 设置状态
     * @param {string} status - 状态
     * @returns {void}
     * @description 设置状态，如果未传入状态，则抛出错误
     * in_progress: 进行中
     * completed: 完成
     * error: 错误
     */
    setStatus(state, { status, sessionId }) {
      state.sessionChatMap[sessionId || state.currentSessionId].status = status;
    },
    setChatLoading(state, { chatLoading, sessionId }) {
      state.sessionChatMap[sessionId || state.currentSessionId].chatLoading = chatLoading;
    },
    /**
     * 添加系统消息
     * @param {string} content - 消息文本
     * @param {string} [sessionId] - 会话 ID，不传则取当前会话
     * @param {'error'|'abort'} [messageType='error'] - error: 服务/网络异常；abort: 用户主动暂停
     */
    // addSystemMessage(state, { content, sessionId, messageType }) {
    //   state.sessionChatMap[sessionId || state.currentSessionId].messageList.push(
    //     [generateSystemMessageTemplate({ content, messageType })]
    //   );
    // },
    // 用户主动暂定消息 / 异常消息等
    addAbortMessage(state, { content, sessionId, messageType = 'abort', role = 'userabort' }) {
      const sid = sessionId || state.currentSessionId;
      if (!state.sessionChatMap[sid]) {
        state.sessionChatMap[sid] = defaultSessionChat();
      }
      const message = state.sessionChatMap[sid].messageList || [];
      const lastMessage = message[message.length - 1];
      const abortMessage = generateSystemMessageTemplate({
        content,
        messageType,
        role
      });

      // 1. 如果最后一个消息是数组，将系统消息，直接添加到数组中
      if (Array.isArray(lastMessage)) {
        lastMessage.push(abortMessage);
      } else {
        // 2. 如果最后一个消息不是数组，将系统消息作为最新消息，push到消息最后一个
        state.sessionChatMap[sid].messageList.push([abortMessage]);
      }
    },
    /**
     * 聊天开始
     * @param {*} state
     * @returns {void}
     */
    chatStart(state, { sessionId }) {
      const sid = sessionId || state.currentSessionId;
      const chat = state.sessionChatMap[sid];
      if (!chat) return;
      // 幂等保护：同一次流式会话多次触发 open/reconnect 时，避免重复 push 同一临时数组导致重复渲染
      const lastMessage = chat.messageList[chat.messageList.length - 1];
      if (lastMessage === chat.tempMessageList) return;
      // chat.messageList.push(chat.tempMessageList);

      if (Array.isArray(lastMessage)) {
        // 替换最后一项（上轮被 chatDone 冻结的部分内容），用新的 tempMessageList 接续流式输出
        chat.messageList.splice(chat.messageList.length - 1, 1, chat.tempMessageList);
      } else {
        chat.messageList.push(chat.tempMessageList);
      }
    },
    /**
     * 聊天完成
     * @param {*} state
     * @returns {void}
     */
    chatDone(state, { sessionId }) {
      const sid = sessionId || state.currentSessionId;
      const chat = state.sessionChatMap[sid];
      if (!chat) return;
      const messageList = chat.messageList;
      if (!messageList.length) return;
      // 只在最后一项仍是 tempMessageList 引用时执行：
      // _error 场景中，_error handler 已调用 chatDone 并 push 了错误消息，
      // 随后 close() → xhr.abort() 又同步触发 _done，此时最后一项已被错误消息占据，
      // 不能再用空 tempML 覆盖它。
      if (messageList[messageList.length - 1] !== chat.tempMessageList) return;
      messageList[messageList.length - 1] = cloneDeep(chat.tempMessageList);
      // 清空临时消息列表
      chat.tempMessageList = [];
    },
    /**
     * 转换消息 chunk
     * @param {object} message - 消息
     * @returns {void}
     * @description 转换消息，如果未传入消息，则抛出错误
     */
    transformMessageChunk(state, { chunk, sessionId }) {
      const { type, status, content, data, text } = chunk;

      const session_id = sessionId || state.currentSessionId;

      const tempMessageList = state.sessionChatMap[session_id].tempMessageList;
      switch (type) {
        // 开始思考 创建思考内容模版
        case 'reasoning':
          if (status === 'in_progress') {
            const lastMsg = tempMessageList[tempMessageList.length - 1];
            if (lastMsg?.type === 'reasoning' && lastMsg?.status === 'in_progress' && lastMsg?.content?.[0]) {
              lastMsg.content[0].status = 'in_progress';
              lastMsg.content[0].text += chunk?.text ?? '';
            } else {
              tempMessageList.push(generateReasoningTemplate(chunk));
            }
          }

          if (status === 'completed' && content) {
            const lastMsg = tempMessageList[tempMessageList.length - 1];
            if (lastMsg?.type === 'reasoning') {
              lastMsg.status = status;
              lastMsg.content = content;
            }
          }
          break;
        case 'text':
          // 判断会话列表最后一项是否完成 status === 'completed' 且类型为思考
          if (status === 'in_progress') {
            // 过滤正文中是 “</think> \n\n” 的数据
            const isThinkTag = chunk.text?.trimStart().startsWith('</think> \n\n ');
            const append = isThinkTag ? '' : (chunk?.text || '') ?? '';
            // text chunk 早于 message 模板到达时 tempMessageList 可能为空，静默跳过
            const lastTextMsg = tempMessageList[tempMessageList.length - 1];
            if (!lastTextMsg?.content?.[0]) break;
            lastTextMsg.content[0].text = (lastTextMsg.content[0].text ?? '') + append;
          }
          break;
        case 'message':
          /** 判断是否是第一次输出 */
          if (status === 'in_progress' && !text) {
            tempMessageList.push(generateMessageTemplate(chunk));
          }

          // 过滤正文中是 “</think> \n\n” 的数据
          if (status === 'completed' && content) {
            if (content[0]?.text?.trimStart().startsWith('</think> \n\n')) {
              return;
            }
            // 判断是否是一次性输出；tempMessageList 为空时跳过状态判断直接 push
            const lastMsgForMessage = tempMessageList[tempMessageList.length - 1];
            if (lastMsgForMessage?.type === 'message' && lastMsgForMessage.status === 'in_progress') {
              // 末尾正是本条正文的 in_progress 占位：置为完成，并用完成帧的完整正文覆盖。
              // 兼容后端「只在完成帧一次性下发完整正文（delta:false）、不发增量 text 帧」的情况，
              // 否则占位 text 恒为空会被渲染层过滤，导致最终结果不展示。
              lastMsgForMessage.status = status;
              // 护栏：仅当完成帧正文非空时才覆盖，避免「完成帧不带正文」清空已累积的增量正文。
              if (content?.[0]?.text?.trim()) {
                lastMsgForMessage.content = content;
              }
            } else {
              tempMessageList.push(generateMessageTemplate(chunk));
              tempMessageList[tempMessageList.length - 1].content = content;
            }
          }
          break;

        /** 工具调用 */
        case 'plugin_call':
          if (status === 'in_progress') {
            tempMessageList.push(generatePluginCallTemplate(chunk));
          }

          if (status === 'completed' && content) {
            const curMessage = tempMessageList.find((item) => item.id === chunk.id);
            if (curMessage) {
              curMessage.content = content;
              curMessage.status = status;
            }
          }

          break;

        case 'data':
          applyOpenClawDataTypeChunk(tempMessageList, status, data);
          break;

        /** 工具调用输出 */
        case 'plugin_call_output':
          applyOpenClawPluginCallOutputChunk(tempMessageList, status, content, data);
          break;
      }
    }
  },
  actions: {
    /**
     * 关闭指定会话的 SSE 连接（仅断开前端监听，不通知后端停止任务）
     * 适用于切换会话时清理旧会话的连接和不活跃定时器
     * @param {string} [sessionId] - 会话ID，默认取 currentSessionId
     */
    closeSessionSse(state, { sessionId } = {}) {
      const sid = sessionId || state.state.currentSessionId;
      // 主动关闭连接时一并终止自动重连窗口（切换会话 / 新建对话 / 手动恢复等）
      clearReconnectController(sid);
      const sessionChat = state.state.sessionChatMap[sid];
      if (!sessionChat?.sseInstance) return;
      // 若流式传输中途关闭，先冻结已接收的部分内容，避免 tempMessageList 活引用残留在 messageList 中
      if (sessionChat.tempMessageList?.length > 0) {
        state.commit('chatDone', { sessionId: sid });
      }
      sessionChat.sseInstance.close();
      state.commit('setSseInstance', { sseInstance: null, sessionId: sid });
    },
    /**
     * 关闭所有会话的 SSE 连接（仅断开前端监听，不通知后端停止任务）
     * 适用于离开 chat 视图或整个 openClaw 模块时的全量清理
     */
    closeAllSessionSse(state) {
      const sessionChatMap = state.state.sessionChatMap;
      Object.keys(sessionChatMap).forEach((sessionId) => {
        // 离开聊天视图时终止所有会话的自动重连窗口
        clearReconnectController(sessionId);
        const sessionChat = sessionChatMap[sessionId];
        if (!sessionChat?.sseInstance) return;
        if (sessionChat.tempMessageList?.length > 0) {
          state.commit('chatDone', { sessionId });
        }
        sessionChat.sseInstance.close();
        state.commit('setSseInstance', { sseInstance: null, sessionId });
      });
    },
    /**
     * 进入/延续断网后的自动重连窗口。
     * 首次网络错误：开启 30s 窗口、维持「执行中」loading，并启动定时轮询重试；
     * 窗口已存在（重连尝试自身又断网）：清除在途标记并安排下一次轮询，且不重置 30s 倒计时。
     * @param {object} state - Vuex action 上下文
     * @param {string} [sessionId] - 会话 ID，默认取当前会话
     * @returns {void}
     */
    startStreamReconnect(state, { sessionId } = {}) {
      const sid = sessionId || state.state.currentSessionId;
      if (!sid) return;

      // 窗口已存在：说明是重连尝试本身再次断网，重置在途标记并继续窗口内轮询，
      // 不新建窗口、不重置截止时间，保证整体仍是「断网起 30s」。
      if (streamReconnectControllers.has(sid)) {
        const exist = streamReconnectControllers.get(sid);
        exist.inFlight = false;
        logOpenClawStream('info', '任务模式-继续等待流式自动重连', {
          sessionId: sid
        });
        scheduleReconnectAttempt(state, sid, RECONNECT_POLL_INTERVAL_MS);
        return;
      }

      streamReconnectControllers.set(sid, {
        deadline: Date.now() + RECONNECT_WINDOW_MS,
        timer: null,
        inFlight: false
      });
      logOpenClawStream('warn', '任务模式-进入流式自动重连窗口', {
        sessionId: sid,
        reconnectWindowMs: RECONNECT_WINDOW_MS
      });

      // 维持「执行中」loading 态，让用户在重连窗口内看到任务仍在进行而非错误
      state.commit('setStatus', { status: 'streaming', sessionId: sid });
      state.commit('setChatLoading', { chatLoading: true, sessionId: sid });

      scheduleReconnectAttempt(state, sid, RECONNECT_POLL_INTERVAL_MS);
    },
    /**
     * 停止对话
     * @param {*} state
     * @returns {void}
     * @description 停止对话，如果未传入会话ID，则抛出错误
     */
    async stopChat(state, { sessionId, chatId } = {}) {
      const sid = sessionId || state.state.currentSessionId;
      // 用户主动停止：终止任何进行中的自动重连窗口
      clearReconnectController(sid);
      const sessionChat = state.state.sessionChatMap[sid];
      if (!sessionChat) return;
      logOpenClawStream('info', '任务模式-用户停止生成', {
        sessionId: sid,
        chatId
      });

      try {
        if (chatId) {
          await api.stopChat(chatId, {});
          logOpenClawStream('info', '任务模式-停止生成接口成功', {
            sessionId: sid,
            chatId
          });
        }
      } catch (error) {
        console.warn('stop chat error', error);
        logOpenClawStream('error', '任务模式-停止生成接口失败', {
          sessionId: sid,
          chatId,
          errorMessage: error?.message,
          errorStack: error?.stack
        });
      }

      sessionChat.sseInstance?.close();
      state.commit('setSseInstance', { sseInstance: null, sessionId: sid });

      if (sessionChat.tempMessageList?.length > 0) {
        state.commit('chatDone', { sessionId: sid });
      }

      // 最后一项没有内容时补齐提示
      // const lastMessage = state.state.sessionChatMap[sid].messageList;
      // if (!lastMessage[lastMessage.length - 1]?.content) {
      //   state.commit('addSystemMessage', { content: '用户已取消对话', sessionId: sid });
      // }

      state.commit('setStatus', { status: 'completed', sessionId: sid });
      state.commit('setChatLoading', { chatLoading: false, sessionId: sid });
      emitter.emit('openClaw/chatStart', 'stop');
    },
    /**
     * 发起对话
     * @param {object} state - 状态
     * @param {string} content - 内容
     * @param {array} attachments - 附件
     * @param {boolean} reconnect - 是否重连
     * @returns {void}
     * @description 发起对话，如果未传入内容，则抛出错误
     */
    async sendMessage(state, { content, attachments, reconnect = false }) {
      // 获取本地用户信息
      const jmUserInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO);
      if (!jmUserInfo) {
        throw new Error('无用户信息');
      }
      // 添加用户消息
      if (!reconnect) {
        state.commit('addUserMessage', {
          content,
          attachments,
          metadataTimestamp: dayjs().format('YYYY-MM-DD HH:mm:ss')
        });
        // 主动提问才算完成「AI 进宝任务模式」新手任务，重连不计入
        reportNewbieTask(NEWBIE_TASK_BIZ_TYPE.AI_TASK_MODE);
      }

      const headers = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
        ...buildHeaders('POST', {})
      };

      const sessionId = state.state.currentSessionId;
      state.commit('setUserSelectedSessionId', sessionId);
      logOpenClawStream('info', reconnect ? '任务模式-发起流式重连请求' : '任务模式-发起流式请求', {
        sessionId,
        reconnect,
        contentLength: typeof content === 'string' ? content.length : 0,
        attachmentCount: Array.isArray(attachments) ? attachments.length : 0,
        channel: 'console'
      });
      const existingSessionChat = state.state.sessionChatMap[sessionId];
      // 同一会话重复恢复时，先关闭旧 SSE，避免同一 chunk 被多监听器重复消费
      if (existingSessionChat?.sseInstance) {
        existingSessionChat.sseInstance.close();
        state.commit('setSseInstance', { sseInstance: null, sessionId });
      }

      let payload = {
        prompt: content,
        input: [
          {
            role: 'user',
            type: 'message',
            content: [
              {
                type: 'text',
                text: content,
                status: 'created'
              },
              ...(attachments || []).map((file) => ({
                type: 'file',
                file_url: file.fileUrl,
                filename: file.fileName || file.name
              }))
            ]
          }
        ],
        session_id: sessionId,
        user_id: state.getters.getCurrentChannelUserId || `${jmUserInfo?.uid || 'default'}`,
        channel: 'console',
        stream: true
      };

      if (reconnect) {
        payload = {
          reconnect,
          session_id: sessionId,
          user_id: state.getters.getCurrentChannelUserId || `${jmUserInfo?.uid || 'default'}`,
          channel: 'console'
        };
      }
      post(OPEN_CLAW_CHAT_API, payload, { headers })
        .then((sseInstance) => {
          sseInstance.addEventListener(`${sessionId}_message`, (event) => {
            try {
              const chunk = JSON.parse(event?.data);
              // reconnect 回放时可能收到 idle 心跳/状态包，不应继续显示“对话中”
              if (chunk?.status === 'idle') {
                state.commit('setStatus', { status: 'completed', sessionId });
                state.commit('setChatLoading', { chatLoading: false, sessionId });
                return;
              }
              if (chunk?.status === 'in_progress') {
                state.commit('setStatus', { status: 'streaming', sessionId });
                // reconnect 仅用于恢复流，不需要刷新会话列表，避免切回会话时额外触发 chats 请求
                if (!reconnect) {
                  emitter.emit('openClaw/chatStart', 'message');
                }
              }
              emitFileGeneratedOnce(sessionId, chunk);
              state.commit('transformMessageChunk', { chunk, sessionId });
            } catch (error) {
              console.log('error', error);
              logOpenClawStream('error', '任务模式-解析流式帧失败', {
                sessionId,
                reconnect,
                errorMessage: error?.message,
                rawLength: typeof event?.data === 'string' ? event.data.length : 0
              });
            }
          });
          sseInstance.addEventListener(`${sessionId}_error`, (event) => {
            logger.log(`${sessionId}_error`, event);
            logOpenClawStream('error', '任务模式-流式错误事件', {
              sessionId,
              reconnect,
              isNetworkError: Boolean(event.isNetworkError),
              dataType: typeof event.data,
              dataLength: typeof event.data === 'string' ? event.data.length : 0
            });

            // 断网/报错时同步冻结已接收的部分内容，防止 tempMessageList 引用残留在 messageList 中：
            // 若不调用 chatDone，下次 chatStart 会把同一 tempML 引用再次 push/splice 进 messageList，
            // 导致同一数组引用出现两次，渲染出重复内容。
            const sessionChatOnError = state.state.sessionChatMap[sessionId];
            if (sessionChatOnError?.tempMessageList?.length > 0) {
              state.commit('chatDone', { sessionId });
            }

            // 网络层错误：window.offline / 不活跃超时（isNetworkError=true）
            // 或 XHR error 事件触发时 response 为空（网络抖动 / TCP 断开）。
            // 不立即报错，而是进入/延续 30s 静默自动重连窗口，窗口内反复调用重试接口重连，
            // 超时仍未恢复才由 failStreamReconnect 落地错误气泡。
            if (event.isNetworkError || event.data === '' || event.data === null) {
              logOpenClawStream('warn', '任务模式-网络断流触发自动重连', {
                sessionId,
                reconnect,
                event
              });
              state.dispatch('startStreamReconnect', { sessionId });
              return;
            }

            // 业务错误（非网络层）：维持原有立即报错逻辑
            state.commit('setStatus', { status: 'completed', sessionId });
            state.commit('setChatLoading', { chatLoading: false, sessionId });

            // event.data 是 XHR 原始响应字符串，需先解析为对象再读取 code/msg
            let parsedData = null;
            try {
              parsedData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            } catch {
              // 响应体不是合法 JSON（如 HTML 错误页），按通用异常处理
            }

            if (parseInt(parsedData?.code) >= 5000) {
              logOpenClawStream('error', '任务模式-流式业务错误', {
                sessionId,
                code: parsedData?.code,
                message: parsedData?.msg || ''
              });
              state.commit('addAbortMessage', {
                content: parsedData.msg || i18n.global.t('openClaw.chatPage.serverBusy'),
                messageType: 'error',
                role: 'system'
              });
            } else {
              state.commit('addAbortMessage', {
                content: i18n.global.t('openClaw.chatPage.serverBusy'),
                messageType: 'error',
                role: 'system'
              });
            }
          });
          sseInstance.addEventListener(`${sessionId}_done`, (event) => {
            logger.log(`${sessionId}_done`, event);
            logOpenClawStream('info', '任务模式-流式完成事件', {
              sessionId,
              reconnect,
              inReconnectWindow: streamReconnectControllers.has(sessionId)
            });
            // 自动重连窗口内：此 done 来自断网后 close()→xhr.abort() 的连锁触发，
            // 并非真实流式结束，需忽略以保持「重连中」loading，待重连成功/超时再决定收尾。
            if (streamReconnectControllers.has(sessionId)) return;
            state.commit('setStatus', { status: 'completed', sessionId });
            state.commit('setChatLoading', { chatLoading: false, sessionId });
            // 临时消息列表深拷贝到消息列表中
            state.commit('chatDone', { sessionId });
            // reconnect 结束后同样需要刷新会话列表（后端 status: running → completed）
            emitter.emit('openClaw/chatStart', 'done');
          });
          sseInstance.addEventListener(`${sessionId}_open`, (event) => {
            logger.log(`${sessionId}_open`, event);
            logOpenClawStream('info', reconnect ? '任务模式-流式重连成功打开' : '任务模式-流式连接打开', {
              sessionId,
              reconnect
            });
            // 流式成功（重新）建立：结束自动重连窗口，避免后续轮询再打断已恢复的流
            clearReconnectController(sessionId);
            state.commit('setStatus', { status: 'streaming', sessionId });
            state.commit('setChatLoading', { chatLoading: true, sessionId });
            // reconnect 恢复流时，移除残留的错误气泡，避免用户同时看到错误提示和正常回复
            if (reconnect) {
              state.commit('removeLastErrorMessage', { sessionId });
            }
            state.commit('chatStart', { sessionId });
            // reconnect 仅恢复流，不触发会话列表刷新
            if (!reconnect) {
              emitter.emit('openClaw/chatStart', 'open');
            }
          });
          sseInstance.addEventListener(`${sessionId}_abort`, (event) => {
            logger.log(`${sessionId}_abort`, event);
            logOpenClawStream('warn', '任务模式-流式中止事件', {
              sessionId,
              reconnect,
              inReconnectWindow: streamReconnectControllers.has(sessionId)
            });
            // 自动重连窗口内：此 abort 由网络错误后的 close()→xhr.abort() 触发，
            // 不能清掉「重连中」loading，否则对话会提前进入结束态。
            if (streamReconnectControllers.has(sessionId)) return;
            state.commit('setStatus', { status: 'completed', sessionId });
            state.commit('setChatLoading', { chatLoading: false, sessionId });
          });
          // 设置 SSE 实例中的sessionId
          sseInstance.sessionId = sessionId;
          // 发送消息
          sseInstance?.stream();
          state.commit('setSseInstance', { sseInstance, sessionId });
        })
        .catch((error) => {
          logger.log('error', error);
          logOpenClawStream('error', reconnect ? '任务模式-发起流式重连失败' : '任务模式-发起流式请求失败', {
            sessionId,
            reconnect,
            errorMessage: error?.message,
            errorStack: error?.stack
          });
          state.commit('setStatus', { status: 'completed', sessionId });
          state.commit('setChatLoading', { chatLoading: false, sessionId });
          emitter.emit('openClaw/chatPostFailed', { sessionId });
          // state.commit('addSystemMessage', { content: '服务异常，请稍后再试' });
        })
        .finally(() => {
          if (!reconnect) emitter.emit('openClaw/chatStart', 'message');
        });
    },

    /**
     * 获取虚拟机过期时间（成功时同步写入 state.assignedMachineTimestamp）
     * @returns {response}
     */
    async getAssignedMachineTimestamp({ commit }) {
      try {
        const response = await getAssignedMachine({}, { hideMessage: true });
        logger.log('openClaw获取虚拟机过期时间成功', response);
        if (
          (response?.code === '0' || response?.code === 0) &&
          response.data !== undefined &&
          response.data !== null &&
          response.data !== ''
        ) {
          commit('setAssignedMachineTimestamp', Number(response.data) || 0);
        }
        return response;
      } catch (error) {
        logger.log('openClaw获取虚拟机过期时间失败', error);
        commit('setAssignedMachineTimestamp', 0);
        throw error;
      }
    },

    /**
     * 是否已分配云电脑资源（投研龙虾 / CoPaw）
     * @returns {Promise<{ data?: unknown, code?: string }>} 与 HTTP 接口返回结构一致；无资源时通常无 data 或 data 为假值
     */
    async checkHasAvailableResource() {
      return new Promise((resolve) => {
        hasAvailableResource({}, { hideMessage: true })
          .then((response) => {
            logger.log('openClaw是否已分配云电脑资源成功', response);
            resolve(response.data ?? false);
          })
          .catch((error) => {
            logger.log('openClaw是否已分配云电脑资源失败', error);
            resolve(false);
          });
      });
    }
  }
};
